import React from 'react';
import moment from 'moment';
import { CacheProvider } from '@emotion/react';
import { Grid, ThemeProvider } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { cacheLtr, cacheRtl } from '../../../../utils/GeneralUtils';

type CustomStaticDatePickerProps = {
  referenceDate: any;
  displayStaticWrapperAs: 'desktop' | 'mobile';
  openTo: 'day' | 'month' | 'year';
  disablePast: boolean;
  value: any;
  minDate: any;
  maxDate: any;
  slots: any;
  slotProps: any;
  onChange: (date: any) => void;
  shouldDisableDate: (date: any) => boolean;
  theme: any;
  dir: 'ltr' | 'rtl';
};

const CustomStaticDatePicker: React.FC<CustomStaticDatePickerProps> = ({
  referenceDate,
  displayStaticWrapperAs,
  openTo,
  disablePast,
  value,
  minDate,
  maxDate,
  slots,
  slotProps,
  onChange,
  shouldDisableDate,
  theme,
  dir,
}) => {
  const now = moment();
  const dayOfMonth = now.date();
  const [viewedMonth, setViewedMonth] = React.useState(() => now.month());
  const useFixedWeeks = dayOfMonth > 20 && viewedMonth === now.month();

  return (
    <CacheProvider value={dir === 'rtl' ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={{ ...theme, direction: dir === 'rtl' ? 'rtl' : 'ltr' }}>
        <Grid container justifyContent="center" mt={2}>
          <StaticDatePicker
            sx={{
              '.MuiPickersCalendarHeader-label': {
                fontSize: '20px',
                fontWeight: 'bold',
              },
              '.MuiDateCalendar-root': {
                backgroundColor: theme.customTheme.palette.background,
                height: 'auto',
              },

              '.MuiPickersDay-dayWithMargin.': {
                color: theme.customTheme.palette.text.primary,
              },
              '.MuiPickersDay-root': {
                fontSize: '16px',
              },
              '.MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': {
                color: theme.customTheme.palette.text.primary,
              },
              '.MuiDayCalendar-weekDayLabel': {
                color: theme.customTheme.palette.text.primary,
              },
              '.MuiPickersArrowSwitcher-button': {
                fontWeight: 'bold',
                color: theme.customTheme.palette.text.primary,
                '& .MuiSvgIcon-root': {
                  fontSize: '32px',
                  fontWeight: 'bold',
                },
              },
              '.MuiPickersArrowSwitcher-button.Mui-disabled': {
                color: theme.customTheme.palette.calendarPicker.disabledArrow,
              },
              '.MuiPickersCalendarHeader-switchViewButton': {
                color: theme.customTheme.palette.text.primary,
              },
              maxWidth: '150px',
              borderRadius: '12px',
              border: `1px solid ${theme.customTheme.palette.border}`,
            }}
            referenceDate={referenceDate}
            displayStaticWrapperAs={displayStaticWrapperAs}
            openTo={openTo}
            disablePast={disablePast}
            value={value}
            minDate={minDate}
            maxDate={maxDate}
            slots={slots}
            slotProps={slotProps}
            onChange={(date: any) => {
              const selectedMonth = moment(date).month();
              if (selectedMonth !== viewedMonth) {
                setViewedMonth(selectedMonth);
              }
              onChange(date);
            }}
            shouldDisableDate={shouldDisableDate}
            onMonthChange={(date: any) => setViewedMonth(moment(date).month())}
            {...(useFixedWeeks && { fixedWeekNumber: 6 })}
            showDaysOutsideCurrentMonth
          />
        </Grid>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default CustomStaticDatePicker;
